import { Link } from "react-router-dom";
import { Users, BookOpen, ClipboardList, ChevronRight, BarChart3, ShieldCheck } from "lucide-react";
import { useChiefGetExaminersQuery, useChiefGetResultsQuery } from "../../api/chiefExaminerApi";

export default function ChiefDashboardPage() {
  const { data: examiners = [] } = useChiefGetExaminersQuery();
  const { data: pending = [] }   = useChiefGetResultsQuery({ status: "submitted" });
  const { data: all = [] }       = useChiefGetResultsQuery();

  const approved   = (all as any[]).filter((r: any) => r.status === "approved").length;
  const published  = (all as any[]).filter((r: any) => r.status === "published").length;

  const stats = [
    { label: "Examiners",       val: examiners.length, icon: Users,        color: "indigo"  },
    { label: "Pending Review",  val: pending.length,   icon: ClipboardList, color: "amber"   },
    { label: "Approved",        val: approved,         icon: BarChart3,    color: "emerald" },
    { label: "Published",       val: published,        icon: BookOpen,     color: "blue"    },
  ];

  const nav = [
    {
      title: "Manage Examiners",
      desc:  `${examiners.length} examiner${examiners.length !== 1 ? "s" : ""} under supervision`,
      icon:  <Users className="w-6 h-6 text-indigo-500" />,
      href:  "/exams/chief/examiners",
      color: "indigo",
    },
    {
      title: "Assign Courses",
      desc:  "Map courses to examiners per diet",
      icon:  <BookOpen className="w-6 h-6 text-purple-500" />,
      href:  "/exams/chief/assign-courses",
      color: "purple",
    },
    {
      title: "Review Results",
      desc:  `${pending.length} batch${pending.length !== 1 ? "es" : ""} awaiting review`,
      icon:  <ClipboardList className="w-6 h-6 text-amber-500" />,
      href:  "/exams/chief/review",
      color: "amber",
      badge: pending.length,
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <BarChart3 className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Chief Examiner</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">Manage your examiners, course assignments, and result reviews.</p>
          </div>
        </div>
      </div>

      {/* Nav cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {nav.map((c) => (
          <Link key={c.href} to={c.href}
            className="relative bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all group flex flex-col gap-4">
            {(c.badge ?? 0) > 0 && (
              <span className="absolute top-4 right-4 min-w-[1.25rem] h-5 px-1.5 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {c.badge}
              </span>
            )}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${c.color}-50`}>
              {c.icon}
            </div>
            <div>
              <p className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{c.title}</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{c.desc}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-indigo-400 transition-colors self-end mt-auto" />
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Review Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Submitted result batches must be reviewed within the stipulated window. Approved batches are forwarded to the
            Chairman of the Moderation Board for final publication.
          </p>
        </div>
      </div>
    </div>
  );
}
